/**
 * AIExpenseCapture — iter195.
 *
 * Compact two-button bar that lets users seed the Add Expense form via:
 *   📷 Snap a receipt  →  POST /api/ai/expense/parse-photo (Gemini/GPT vision)
 *   🎤 Speak           →  POST /api/ai/expense/parse-voice (Whisper + GPT)
 *
 * On a successful parse the parent receives an `onParsed(payload)` callback
 * with the same shape both endpoints return — `{vendor, amount, currency,
 * date, category, line_items, raw_text, confidence}`. Confidence <0.4 surfaces
 * a "Please review" badge so the user doesn't blindly trust the prefill.
 */
import { useEffect, useRef, useState } from 'react';
import { Camera, Mic, Sparkles, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const MAX_VOICE_SECONDS = 25; // soft cap, server enforces 25 MB
const LOW_CONFIDENCE = 0.4;

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const out = String(reader.result || '');
      const comma = out.indexOf(',');
      resolve(comma >= 0 ? out.slice(comma + 1) : out);
    };
    reader.readAsDataURL(file);
  });
}

export default function AIExpenseCapture({ groupCurrency = 'INR', onParsed, disabled = false }) {
  const [busy, setBusy] = useState(null); // 'photo' | 'voice' | null
  const [recording, setRecording] = useState(false);
  const [recordedSec, setRecordedSec] = useState(0);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tickRef = useRef(null);

  useEffect(() => () => {
    // Cleanup on unmount: stop any active stream + interval
    if (tickRef.current) clearInterval(tickRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }
  }, []);

  const dispatchParsed = (payload) => {
    if (!onParsed) return;
    onParsed(payload);
    if ((payload?.confidence ?? 0) < LOW_CONFIDENCE) {
      toast.warning('Low confidence — please review the fields before saving.');
    } else {
      toast.success('Filled in from your ' + (payload?.raw_text ? 'voice note' : 'receipt'));
    }
  };

  // ---------- Photo ----------
  const handlePhotoPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image too large — please pick one under 10 MB');
      return;
    }
    setBusy('photo');
    try {
      const image_base64 = await fileToBase64(file);
      const res = await api.post('/ai/expense/parse-photo', {
        image_base64,
        mime_type: file.type || 'image/jpeg',
        group_currency: groupCurrency,
      });
      dispatchParsed(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not read that receipt — try a clearer photo');
    } finally {
      setBusy(null);
    }
  };

  // ---------- Voice ----------
  const startRecording = async () => {
    if (recording) return;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      toast.error('Voice capture is not supported on this browser');
      return;
    }
    if (typeof window === 'undefined' || !window.MediaRecorder) {
      toast.error('Voice capture is not supported on this browser');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm'
        : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4'
        : '';
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (ev) => { if (ev.data?.size) chunksRef.current.push(ev.data); };
      recorder.onstop = async () => {
        setRecording(false);
        if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
        const tracks = streamRef.current?.getTracks() || [];
        tracks.forEach((t) => t.stop());
        streamRef.current = null;

        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        chunksRef.current = [];
        if (!blob.size) {
          toast.error("Couldn't record audio. Try again.");
          return;
        }
        setBusy('voice');
        try {
          const ext = (recorder.mimeType || 'audio/webm').includes('mp4') ? 'mp4' : 'webm';
          const form = new FormData();
          form.append('audio', blob, `voice.${ext}`);
          form.append('group_currency', groupCurrency);
          form.append('reference_date', new Date().toISOString().slice(0, 10));
          const res = await api.post('/ai/expense/parse-voice', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000,
          });
          dispatchParsed(res.data);
        } catch (err) {
          toast.error(err?.response?.data?.detail || 'Could not understand the recording — try again');
        } finally {
          setBusy(null);
        }
      };
      recorder.start();
      setRecording(true);
      setRecordedSec(0);
      tickRef.current = setInterval(() => {
        setRecordedSec((s) => {
          const next = s + 1;
          if (next >= MAX_VOICE_SECONDS) {
            try { recorder.stop(); } catch { /* ignore */ }
          }
          return next;
        });
      }, 1000);
    } catch (err) {
      toast.error(err?.name === 'NotAllowedError' ? 'Microphone access denied' : 'Could not start recording');
      setRecording(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      try { mediaRecorderRef.current.stop(); } catch { /* ignore */ }
    }
  };

  const isBusy = !!busy || disabled;

  return (
    <div className="rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 px-3 py-2.5" data-testid="ai-expense-capture">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-3.5 h-3.5 text-primary" />
        <span className="text-xs uppercase tracking-[0.16em] font-bold text-primary">AI quick fill</span>
      </div>
      <div className="flex gap-2">
        {/* Photo */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          className="hidden"
          onChange={handlePhotoPicked}
          data-testid="ai-expense-photo-input"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isBusy}
          data-testid="ai-expense-photo-btn"
          className="flex-1 rounded-lg border-2 border-primary/30 bg-white dark:bg-card hover:bg-primary/10 active:scale-[0.99] transition px-3 py-2 text-[12px] font-bold flex items-center justify-center gap-1.5 disabled:opacity-60"
        >
          {busy === 'photo' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
          {busy === 'photo' ? 'Reading…' : 'Snap receipt'}
        </button>
        {/* Voice */}
        {!recording ? (
          <button
            type="button"
            onClick={startRecording}
            disabled={isBusy}
            data-testid="ai-expense-voice-btn"
            className="flex-1 rounded-lg border-2 border-primary/30 bg-white dark:bg-card hover:bg-primary/10 active:scale-[0.99] transition px-3 py-2 text-[12px] font-bold flex items-center justify-center gap-1.5 disabled:opacity-60"
          >
            {busy === 'voice' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mic className="w-3.5 h-3.5" />}
            {busy === 'voice' ? 'Transcribing…' : 'Speak'}
          </button>
        ) : (
          <button
            type="button"
            onClick={stopRecording}
            data-testid="ai-expense-voice-stop-btn"
            className="flex-1 rounded-lg border-2 border-rose-500 bg-rose-50 dark:bg-rose-950/40 active:scale-[0.99] transition px-3 py-2 text-[12px] font-bold flex items-center justify-center gap-1.5 text-rose-700 dark:text-rose-200 animate-pulse"
          >
            <X className="w-3.5 h-3.5" />
            Stop ({MAX_VOICE_SECONDS - recordedSec}s)
          </button>
        )}
      </div>
    </div>
  );
}
